import { useQContent } from '@exam-management/common/qcontent-provider';
import {
  GetQuestionByIdDocument,
  QuestionGroupType,
  type QuestionInputData,
  QuestionPType,
  QuestionStatus,
  useSaveQuestionAsDraftMutation,
  useSaveQuestionAsUnpublishedMutation,
} from '@exam-management/graphql';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { useSnackbar } from '@shared/snackbar';
import { useCallback } from 'react';

import { type QContentDetailFormContextData } from '../../types';

interface UseUserDetailsFormSubmissionParams {
  contentId: string;
}

export default function useSaveQuestionFormSubmission(params: UseUserDetailsFormSubmissionParams) {
  const { contentId } = params;
  const { showSnackbar } = useSnackbar();
  const { qContent } = useQContent();

  const mutationOptions = {
    onCompleted: () => {
      showSnackbar({
        message: 'Question has been saved',
        variant: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        message: 'Unable to Save Question',
        variant: 'error',
      });
    },
  };

  const [saveQuestionAsDraft] = useSaveQuestionAsDraftMutation({
    ...mutationOptions,
    awaitRefetchQueries: true,
    refetchQueries: [GetQuestionByIdDocument],
  });
  const [saveQuestionAsUnpublished] = useSaveQuestionAsUnpublishedMutation({
    ...mutationOptions,
    awaitRefetchQueries: true,
    refetchQueries: [GetQuestionByIdDocument],
  });

  const handleSubmit = useCallback(
    async (data: QContentDetailFormContextData) => {
      const qBankStructureLevelItemId = data.location.SUB_TOPIC?.value as string;

      if (!qBankStructureLevelItemId) return null;

      const isCalculated =
        data.information.calculated === '' ? null : data.information.calculated === 'true';
      const pValue =
        data.information.pValue.selected === 'pInitial'
          ? data.information.pValue.pInitial
          : data.information.pValue.pStat;
      const group =
        data.location.LOQ.selected === 'true'
          ? {
              name: data.location.LOQ.value?.value as string,
              type: QuestionGroupType.LotOfQuestion,
            }
          : {
              type: QuestionGroupType.Single,
            };
      const cognitiveDomains: string[] = data.properties.cognitiveDomain.map(
        (domain: { label: string; value: string }) => domain.value,
      );
      const convertedData = {
        details: parseContentForApi(data.detail.details),
        choices: data.detail.choices.map((choice) => ({
          ...choice,
          label: parseContentForApi(choice.label),
        })),
        correctAnswer: data.detail.correctAnswer,
        calculated: isCalculated,
        pType:
          data.information.pValue.selected === 'pStat'
            ? QuestionPType.PStat
            : QuestionPType.PInitial,
        pValue: pValue ?? null,
        pInitial: pValue ?? null,
        bIndexValue: data.information.bIndex,
        cognitiveDomains,
        learningObjectiveStatement: data.properties.learningObjectiveStatement?.value ?? undefined,
        solution: parseContentForApi(data.properties.solution),
        reference: parseContentForApi(data.properties.reference),
        group,
        pStats: data.information.pStats,
        ...data.owners,
      } as QuestionInputData;

      if (qContent.status === QuestionStatus.Unpublished) {
        const convertedDataWithGroup = {
          ...convertedData,
          group: { ...convertedData.group, id: qContent.questionGroup?.id },
        };
        const saveResult = await saveQuestionAsUnpublished({
          variables: {
            input: {
              id: contentId,
              data: convertedDataWithGroup,
            },
          },
        });
        return saveResult?.data?.qctSaveQuestionAsUnpublish.id;
      } else {
        const saveResult = await saveQuestionAsDraft({
          variables: {
            input: {
              qBankStructureLevelItemId,
              id: contentId,
              data: convertedData,
            },
          },
        });
        return saveResult?.data?.qctSaveQuestionAsDraft.id;
      }
    },
    [
      saveQuestionAsUnpublished,
      contentId,
      qContent.status,
      qContent.questionGroup?.id,
      saveQuestionAsDraft,
    ],
  );

  return {
    handleSubmit,
  };
}
